import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { filter, Subject, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { Status } from 'app/providers/storage-api/actionplan.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { getMinDueDate, getStatusDs } from 'app/utils/action-plan.utils';
import { ExtractFormControl } from 'app/utils/validator.utils';
import { ActionItemDto } from '../action-plan-dialog/action-plan-dialog.component';

interface ActionItemEditorForm {
  name: FormControl<string>;
  assignee: FormControl<string>;
  dueDate: FormControl<Date>;
  status: FormControl<Status>;
  notes: FormControl<string>;
}

export type ActionItemModel = ExtractFormControl<ActionItemEditorForm>;

export type ActionItemEditorMode = 'none' | 'create' | 'edit';

@EnableClassTelemetry()
@Component({
  selector: 'app-action-item-editor',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './action-item-editor.component.html',
  styleUrls: ['./action-item-editor.component.scss'],
})
export class ActionItemEditorComponent implements OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() mode: ActionItemEditorMode;
  @Input() item?: ActionItemDto;

  @Output() save = new EventEmitter<ActionItemModel>();
  @Output() cancel = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() valueChange = new EventEmitter<void>();

  form: FormGroup<ActionItemEditorForm>;

  statusDs: DropdownObject[] = getStatusDs();
  minDueDate: Date = getMinDueDate();

  isValueChanged = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder) {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.shouldPatchItemValues(changes)) {
      this.patchFormValues();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onSave(): void {
    const actionItem = this.form.getRawValue() as ActionItemModel;

    this.save.emit(actionItem);

    this.reset();
  }

  onCancel(): void {
    this.cancel.emit();

    this.reset();
  }

  onDelete(): void {
    this.delete.emit();

    this.reset();
  }

  get isInNoneMode(): boolean {
    return this.mode === 'none';
  }

  get isDeleteDisabled(): boolean {
    return this.mode !== 'edit';
  }

  get isCancelDisabled(): boolean {
    return this.isNotChangedYet;
  }

  get isSaveDisabled(): boolean {
    return this.isNotChangedYet || this.form.invalid;
  }

  private get isNotChangedYet(): boolean {
    return !this.isValueChanged;
  }

  private buildForm(): void {
    this.form = this.formBuilder.group(<ActionItemEditorForm>{
      name: new FormControl('', Validators.required),
      assignee: new FormControl(''),
      dueDate: new FormControl(new Date(), Validators.required),
      status: new FormControl({ value: Status.NEW, disabled: true }, Validators.required),
      notes: new FormControl(''),
    });

    this.form.valueChanges
      .pipe(
        filter(() => this.isNotChangedYet),
        takeUntil(this._destroy$)
      )
      .subscribe(() => this.handleFormValueChange());
  }

  private shouldPatchItemValues(changes: SimpleChanges): boolean {
    return changes.item && !changes.item.firstChange && changes.item.currentValue;
  }

  private patchFormValues(): void {
    this.form.patchValue(
      {
        name: this.item.name,
        assignee: this.item.assignee_user_name,
        dueDate: new Date(this.item.due_date),
        status: this.item.status,
        notes: this.item.notes,
      },
      { emitEvent: false }
    );

    if (!this.item.isNew) {
      this.form.controls.status.enable({ emitEvent: false });
    }
  }

  private reset(): void {
    this.form.reset(undefined, { emitEvent: false });

    this.isValueChanged = false;
  }

  private handleFormValueChange(): void {
    this.isValueChanged = true;

    this.valueChange.emit();
  }
}
